// Form state, validation and payload transformation for Opportunity dialogs.
// Pure functions only — no React. The server remains authoritative for all
// computed values (first_12_month_revenue) and validation.
import { toast } from "sonner";
import api from "@/lib/api";

export const NONE = "__none__";

export async function fetchOppRefsInto(setRefs, needProspects) {
  try {
    const reqs = [
      api.get("/reference/pipeline-stages"),
      api.get("/reference/lead-sources"),
      api.get("/reference/service-divisions"),
      api.get("/users/options"),
    ];
    if (needProspects) reqs.push(api.get("/prospects"));
    const [stages, sources, divisions, users, prospects] = await Promise.all(reqs);
    setRefs({
      stages: stages.data, sources: sources.data, divisions: divisions.data,
      users: users.data, prospects: prospects ? prospects.data : [],
    });
  } catch (e) {
    toast.error("Opportunity options could not be loaded. Please refresh.");
  }
}

export async function fetchProspectChildrenInto(setChildren, prospectId) {
  if (!prospectId) return setChildren({ contacts: [], properties: [] });
  try {
    const [contacts, properties] = await Promise.all([
      api.get(`/prospects/${prospectId}/contacts`),
      api.get(`/prospects/${prospectId}/properties`),
    ]);
    setChildren({ contacts: contacts.data, properties: properties.data });
  } catch (e) {
    toast.error("Organization contacts/properties could not be loaded.");
  }
}

// --- Form initialization -----------------------------------------------------
export function buildOpportunityForm(opportunity, presetProspectId) {
  const a = opportunity || {};
  return {
    name: a.name || "",
    prospect_id: a.prospect_id || presetProspectId || "",
    owner_id: a.owner_id || "",
    lead_source_id: a.lead_source_id || NONE,
    service_division_ids: a.service_division_ids || [],
    property_ids: a.property_ids || [],
    all_properties: a.all_properties || false,
    primary_contact_id: a.primary_contact_id || NONE,
    pipeline_stage_id: a.pipeline_stage_id || "",
    opportunity_type: a.opportunity_type || "contract",
    target_close_date: a.target_close_date || "",
    bid_due_date: a.bid_due_date || "",
    service_start_date: a.service_start_date || "",
    monthly_recurring_revenue: a.monthly_recurring_revenue ?? "",
    one_time_revenue: a.one_time_revenue ?? "",
    contract_term_months: a.contract_term_months ?? "",
    closing_notes: a.closing_notes || "",
    origin_classification: a.origin_classification || "",
    origin_justification: a.origin_justification || "",
    commission_category: a.commission_category || "",
  };
}

// --- Payload transformation --------------------------------------------------
const numberOrNull = (v) => (v === "" ? null : Number(v));
const idOrNull = (v) => (v === NONE ? null : v);

export function buildOpportunityPayload(form, isAdmin, editing) {
  const tag = form.opportunity_type === "tag";
  const body = {
    name: form.name.trim(),
    opportunity_type: form.opportunity_type,
    lead_source_id: idOrNull(form.lead_source_id),
    service_division_ids: form.service_division_ids,
    property_ids: form.all_properties ? [] : form.property_ids,
    all_properties: form.all_properties,
    primary_contact_id: idOrNull(form.primary_contact_id),
    target_close_date: form.target_close_date || null,
    bid_due_date: form.bid_due_date || null,
    service_start_date: form.service_start_date || null,
    monthly_recurring_revenue: tag ? null : numberOrNull(form.monthly_recurring_revenue),
    one_time_revenue: tag ? numberOrNull(form.one_time_revenue) : null,
    contract_term_months: tag ? null : numberOrNull(form.contract_term_months),
    closing_notes: form.closing_notes || null,
    origin_classification: form.origin_classification || null,
    origin_justification: form.origin_justification || null,
    commission_category: form.commission_category || null,
  };
  if (!editing) {
    body.prospect_id = form.prospect_id;
    if (form.pipeline_stage_id) body.pipeline_stage_id = form.pipeline_stage_id;
  }
  if (isAdmin && form.owner_id) body.owner_id = form.owner_id;
  return body;
}

export function buildCloseWonForm(opportunity) {
  const o = opportunity || {};
  return {
    service_start_date: o.service_start_date || "",
    monthly_recurring_revenue: o.monthly_recurring_revenue ?? "",
    one_time_revenue: o.one_time_revenue ?? "",
    contract_term_months: o.contract_term_months ?? "",
    origin_classification: o.origin_classification || "",
    origin_justification: o.origin_justification || "",
    closing_notes: o.closing_notes || "",
  };
}

export function buildCloseWonPayload(form, version) {
  const payload = {
    service_start_date: form.service_start_date || null,
    monthly_recurring_revenue: numberOrNull(form.monthly_recurring_revenue),
    one_time_revenue: numberOrNull(form.one_time_revenue),
    contract_term_months: numberOrNull(form.contract_term_months),
    closing_notes: form.closing_notes || null,
    version,
  };
  // Only send the classification when chosen — never overwrite an existing one with null.
  if (form.origin_classification) {
    payload.origin_classification = form.origin_classification;
    payload.origin_justification = form.origin_justification?.trim() || null;
  }
  return payload;
}

// --- Validation (client-side convenience; server re-validates) ---------------
export function validateOpportunityForm(form, editing, propertiesCount) {
  if (!form.name.trim()) return "Opportunity name is required";
  if (!editing && !form.prospect_id) return "Select an organization for this opportunity";
  if (!form.all_properties && form.property_ids.length === 0)
    return "Select at least one property (or choose All properties)";
  if (form.all_properties && propertiesCount === 0)
    return '"All properties" needs the organization to have at least one actual property — add the service location first';
  return null;
}

// --- Role-dependent copy for the Closed-Won workflow --------------------------
export const WON_COPY = {
  admin: {
    title: "Confirm Closed – Won",
    description: "Confirming records the win on this opportunity. If the organization is still a Prospect, it becomes a Client in the same step — the green cue follows automatically.",
    action: "confirm-close-won",
    submit: "Confirm Closed – Won",
    success: "Closed – Won confirmed — the organization is now a Client.",
  },
  salesperson: {
    title: "Prepare Closed – Won",
    description: "An administrator must confirm the win. Complete the required fields below.",
    action: "request-close-won",
    submit: "Request Closed – Won",
    success: "Prepared for closing — an administrator will confirm Closed – Won.",
  },
};

export const OPP_DIALOG_COPY = {
  edit: {
    title: "Edit Opportunity",
    description: "Stage changes are made from the pipeline controls, never here.",
    submit: "Save opportunity",
    success: "Opportunity updated",
  },
  create: {
    title: "New Opportunity",
    description: "A distinct deal under an organization — one prospect can carry many.",
    submit: "Create opportunity",
    success: "Opportunity created",
  },
};
